import { useState, useEffect, useRef } from 'react';
import { MODE_LIVRAISON, MODE_MAGASIN, magasinsActifs } from '../lib/magasins';
import { signalerTunnel, arriveeDeLaVisite } from '../lib/mesure';
import { prixLigne } from '../contexts/CartContext';
import { pixelTunnel } from '../lib/pixels';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CreditCard, Truck, ArrowLeft, Loader2 } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { resolveImageUrl } from '../lib/imageUrl';
import { formatPrice } from '../lib/formatPrice';
import {
  determinerZone,
  fraisLivraison,
  libelleZone,
  montantManquant,
  MINIMUM_HORS_LIEGE,
  delaiLivraison,
} from '../lib/delivery';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAYPAL_CLIENT_ID = 'sb'; // Sandbox client ID for testing

// Moyens de paiement activés à l'affichage.
// Mollie (Bancontact + carte) est le paiement en ligne actif.
// Stripe et PayPal restent désactivés (non finalisés).
const PAYMENT_METHODS_ENABLED = {
  mollie: true,
  stripe: false,
  paypal: false,
  cash: true,
};

const TEXTES = {
  fr: {
    panierVide: 'Votre panier est vide',
    voirProduits: 'Voir les produits',
    paiementEnLigne: 'Paiement en ligne sécurisé',
    paiementPaypal: 'Paiement sécurisé via PayPal',
    paiementEspeces: 'Payez en espèces à la livraison',
    resume: 'Résumé de la commande',
    commandeConfirmee: 'Commande confirmée ! Paiement à la livraison.',
    erreur: 'Une erreur est survenue',
    paypalReussi: 'Paiement PayPal réussi !',
    paypalErreur: 'Erreur lors du paiement PayPal',
    adressePlaceholder: 'Rue, numéro, boîte',
    continuerPaypal: 'Continuer avec PayPal',
    quantite: 'Qté :',
    retour: 'Retour',
  },
  en: {
    panierVide: 'Your cart is empty',
    voirProduits: 'Browse products',
    paiementEnLigne: 'Secure online payment',
    paiementPaypal: 'Secure payment via PayPal',
    paiementEspeces: 'Pay in cash on delivery',
    resume: 'Order summary',
    commandeConfirmee: 'Order confirmed! Payment on delivery.',
    erreur: 'Something went wrong',
    paypalReussi: 'PayPal payment successful!',
    paypalErreur: 'PayPal payment error',
    adressePlaceholder: 'Street, number, box',
    continuerPaypal: 'Continue with PayPal',
    quantite: 'Qty:',
    retour: 'Back',
  },
};

export default function Checkout() {
  const { t, getLocalizedField, language } = useLanguage();
  const x = TEXTES[language] || TEXTES.fr;
  const { items, subtotal, clearCart } = useCart();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    delivery_address: '',
    delivery_city: '',
    delivery_postal: '',
    payment_method: 'mollie',
  });
  
  // ── Livraison ou retrait en magasin ──
  // Une liste de magasins dès maintenant, bien qu'il n'y en ait qu'un : le
  // jour où un deuxième ouvre, le choix apparaît sans retoucher ce tunnel.
  const magasins = magasinsActifs();
  const [modeRetrait, setModeRetrait] = useState(MODE_LIVRAISON);
  const [magasinId, setMagasinId] = useState(magasins[0]?.id || '');
  const retrait = modeRetrait === MODE_MAGASIN;
  const magasinChoisi = magasins.find((m) => m.id === magasinId) || magasins[0] || null;

  // ── Code promotionnel ──
  // Le serveur fait foi : on lui soumet le panier, il répond ce que le code
  // donne. Rien n'est calculé ici, l'affichage reflète sa réponse.
  const [codeSaisi, setCodeSaisi] = useState('');
  const [promo, setPromo] = useState(null);
  const [promoErreur, setPromoErreur] = useState('');
  const [promoEnCours, setPromoEnCours] = useState(false);

  const remise = promo?.remise ?? 0;
  const portOffert = promo?.livraison_offerte ?? false;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPayPal, setShowPayPal] = useState(false);
  const [orderId, setOrderId] = useState(null);

  // La zone est déduite du code postal, qui est numérique et sans ambiguïté.
  // Le nom de ville ne sert que de secours : « Luik », « Liége » ou une faute
  // de frappe faisaient facturer 14,90 € au lieu de 5,90 €.
  const zone = determinerZone(formData.delivery_postal, formData.delivery_city);
  const deliveryFee = fraisLivraison(zone);
  // Rien ne se déplace pour un retrait : ni frais, ni minimum de commande.
  const manquant = retrait ? 0 : montantManquant(zone, subtotal - remise);
  const minimumNonAtteint = manquant > 0;

  // Tant que l'adresse n'est pas renseignée, on n'affiche pas de frais : mieux
  // vaut « à calculer » qu'un montant qui augmentera en cours de saisie.
  const fraisFinal = (retrait || portOffert) ? 0 : deliveryFee;
  const total = subtotal - remise + (fraisFinal ?? 0);

  const verifierCode = async (code, { discret = false } = {}) => {
    const propre = (code || '').trim();
    if (!propre) return;
    setPromoEnCours(true);
    if (!discret) setPromoErreur('');
    try {
      const { data } = await axios.post(`${API}/promotions/verifier`, {
        code: propre,
        items: items.map((i) => ({ product_id: i.product.id, quantity: i.quantity })),
        delivery_postal: formData.delivery_postal,
        delivery_city: formData.delivery_city,
        mode_retrait: modeRetrait,
        customer_email: formData.customer_email,
      });
      setPromo(data);
      setPromoErreur('');
    } catch (err) {
      setPromo(null);
      setPromoErreur(err?.response?.data?.detail
        || (language === 'fr' ? 'Code invalide.' : 'Invalid code.'));
    } finally {
      setPromoEnCours(false);
    }
  };

  // Un code peut être réservé à une zone ou exiger un panier minimum : si
  // l'adresse ou le panier change après son application, il faut le
  // revalider — sinon l'écran afficherait une remise que le serveur
  // refuserait au moment de la commande.
  const codeApplique = promo?.code;
  useEffect(() => {
    if (!codeApplique) return;
    verifierCode(codeApplique, { discret: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeApplique, zone, subtotal, modeRetrait]);

  // Ouverture du tunnel : comptée une seule fois, à l'arrivée sur la page et
  // seulement si le panier n'est pas vide — sinon on compterait des visiteurs
  // qui n'ont rien à commander.
  const tunnelCompte = useRef(false);
  useEffect(() => {
    if (tunnelCompte.current || items.length === 0) return;
    tunnelCompte.current = true;
    signalerTunnel();
    pixelTunnel({ total, nombreArticles: items.length });
  }, [items.length, total]);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    
    if (e.target.name === 'payment_method') {
      setShowPayPal(e.target.value === 'paypal');
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (items.length === 0) {
      toast.error(x.panierVide);
      return;
    }
    
    // Filet de sécurité : le minimum est déjà annoncé dans le panier et le
    // bouton est désactivé, mais la règle doit rester vérifiée ici aussi.
    if (minimumNonAtteint) {
      toast.error(
        `Commande minimum de ${formatPrice(MINIMUM_HORS_LIEGE, language)} pour la livraison hors région liégeoise`
      );
      return;
    }

    setIsSubmitting(true);
    
    try {
      const orderData = {
        items: items.map(item => ({
          product_id: item.product.id,
          // Seul l'identifiant circule : le prix est décidé par le serveur.
          declinaison_id: item.declinaison?.id || null,
          quantity: item.quantity
        })),
        ...formData,
        // D'où vient ce client : relevé à son arrivée sur le site, transmis
        // ici pour être figé sur la commande. C'est la seule mesure qui dit
        // ce qui fait vraiment vendre.
        mode_retrait: modeRetrait,
        magasin_id: retrait ? (magasinChoisi?.id || '') : '',
        code_promo: promo?.code || null,
        arrivee: arriveeDeLaVisite(),
        origin_url: window.location.origin
      };
      
      const response = await axios.post(`${API}/orders`, orderData);
      
      if ((formData.payment_method === 'mollie' || formData.payment_method === 'stripe') && response.data.checkout_url) {
        // Redirection vers la page de paiement (Mollie / Stripe)
        window.location.href = response.data.checkout_url;
      } else if (formData.payment_method === 'paypal') {
        setOrderId(response.data.order_id);
        setShowPayPal(true);
      } else if (formData.payment_method === 'cash') {
        clearCart();
        toast.success(x.commandeConfirmee);
        navigate(`/checkout/success?method=cash&order_id=${response.data.order_id}`);
      }
    } catch (error) {
      // Error handled silently
      toast.error(error.response?.data?.detail || x.erreur);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handlePayPalApprove = async (data) => {
    try {
      await axios.post(`${API}/paypal/capture?order_id=${orderId}&paypal_order_id=${data.orderID}`);
      clearCart();
      toast.success(x.paypalReussi);
      navigate('/checkout/success?method=paypal');
    } catch (error) {
      // Error handled silently
      toast.error(x.paypalErreur);
    }
  };
  
  if (items.length === 0) {
    return (
      <div className="min-h-screen pt-20 lg:pt-24 flex items-center justify-center" data-testid="checkout-empty">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-text_primary mb-4">{x.panierVide}</h2>
          <button
            onClick={() => navigate('/products')}
            className="px-6 py-3 bg-primary text-white rounded-full font-medium hover:bg-primary_hover transition-colors"
            data-testid="go-to-products-button"
          >
            {x.voirProduits}
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen pt-20 lg:pt-24 pb-12" data-testid="checkout-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-text_secondary hover:text-text_primary transition-colors mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          {x.retour}
        </button>
        
        <h1 className="text-3xl font-bold font-heading text-text_primary mb-8" data-testid="checkout-title">
          {t('checkoutTitle')}
        </h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-8" data-testid="checkout-form">
              {/* Customer Info */}
              <div className="bg-white p-6 rounded-2xl shadow-sm">
                <h2 className="text-lg font-semibold text-text_primary mb-4">{t('customerInfo')}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-text_primary mb-2">{t('fullName')} *</label>
                    <input
                      type="text"
                      name="customer_name"
                      autoComplete="name"
                      value={formData.customer_name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                      data-testid="checkout-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text_primary mb-2">{t('email')} *</label>
                    <input
                      type="email"
                      name="customer_email"
                      autoComplete="email"
                      value={formData.customer_email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                      data-testid="checkout-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text_primary mb-2">{t('phone')} *</label>
                    <input
                      type="tel"
                      name="customer_phone"
                      autoComplete="tel"
                      inputMode="tel"
                      value={formData.customer_phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                      data-testid="checkout-phone-input"
                    />
                  </div>
                </div>
              </div>
              
              {/* Livraison ou retrait */}
              <div className="bg-white p-6 rounded-2xl shadow-sm">
                <h2 className="text-lg font-semibold text-text_primary mb-4">
                  {language === 'fr' ? 'Comment récupérer votre commande' : 'How to get your order'}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" role="radiogroup"
                     aria-label={language === 'fr' ? 'Mode de retrait' : 'Collection method'}>
                  {[
                    { id: MODE_LIVRAISON,
                      titre: language === 'fr' ? 'Livraison à domicile' : 'Home delivery',
                      detail: language === 'fr' ? 'Frais selon votre code postal' : 'Fee based on your postcode' },
                    { id: MODE_MAGASIN,
                      titre: language === 'fr' ? 'Retrait en magasin' : 'Collect in store',
                      detail: language === 'fr' ? 'Gratuit, sans minimum' : 'Free, no minimum' },
                  ].map((choix) => (
                    <button
                      key={choix.id}
                      type="button"
                      role="radio"
                      aria-checked={modeRetrait === choix.id}
                      onClick={() => setModeRetrait(choix.id)}
                      className={`p-4 text-left border rounded-xl transition ${
                        modeRetrait === choix.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-text_primary/40'
                      }`}
                      data-testid={`mode-${choix.id}`}
                    >
                      <span className="block font-semibold text-text_primary">{choix.titre}</span>
                      <span className="block text-sm text-text_secondary mt-0.5">{choix.detail}</span>
                    </button>
                  ))}
                </div>

                {retrait && magasinChoisi && (
                  <div className="mt-4 p-4 bg-secondary/30 rounded-xl text-sm" data-testid="magasin-choisi">
                    {magasins.length > 1 && (
                      <select
                        value={magasinId}
                        onChange={(e) => setMagasinId(e.target.value)}
                        className="w-full mb-3 px-3 py-2 rounded-lg border border-border bg-white"
                        aria-label={language === 'fr' ? 'Choisir le magasin' : 'Choose the store'}
                      >
                        {magasins.map((m) => (
                          <option key={m.id} value={m.id}>{m.nom} — {m.adresse}, {m.ville}</option>
                        ))}
                      </select>
                    )}
                    <p className="font-semibold text-text_primary">{magasinChoisi.nom}</p>
                    <p className="text-text_secondary">
                      {magasinChoisi.adresse}, {magasinChoisi.code_postal} {magasinChoisi.ville}
                    </p>
                    <p className="text-text_secondary mt-1">
                      {language === 'en' ? magasinChoisi.horaires_en : magasinChoisi.horaires_fr}
                    </p>
                    <p className="text-text_secondary mt-2">
                      {language === 'fr'
                        ? 'Vous pouvez passer récupérer votre commande en magasin immédiatement.'
                        : 'You can collect your order in store right away.'}
                    </p>
                  </div>
                )}
              </div>

              {/* Delivery Info */}
              <div className={`bg-white p-6 rounded-2xl shadow-sm ${retrait ? 'hidden' : ''}`}>
                <h2 className="text-lg font-semibold text-text_primary mb-4">{t('deliveryAddress')}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-text_primary mb-2">{t('deliveryAddress')} *</label>
                    <input
                      type="text"
                      name="delivery_address"
                      autoComplete="street-address"
                      value={formData.delivery_address}
                      onChange={handleChange}
                      required={!retrait}
                      placeholder={x.adressePlaceholder}
                      className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                      data-testid="checkout-address-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text_primary mb-2">{t('city')} *</label>
                    <input
                      type="text"
                      name="delivery_city"
                      autoComplete="address-level2"
                      value={formData.delivery_city}
                      onChange={handleChange}
                      required={!retrait}
                      className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                      data-testid="checkout-city-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text_primary mb-2">{t('postalCode')} *</label>
                    <input
                      type="text"
                      name="delivery_postal"
                      autoComplete="postal-code"
                      inputMode="numeric"
                      value={formData.delivery_postal}
                      onChange={handleChange}
                      required={!retrait}
                      className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                      data-testid="checkout-postal-input"
                    />
                  </div>
                </div>

                {/* Zone et frais annoncés dès la saisie du code postal, et non
                    au moment de valider la commande. */}
                {zone && !retrait && (
                  <div
                    className={`mt-4 rounded-xl p-3 text-sm ${
                      minimumNonAtteint ? 'bg-orange-50 text-orange-800' : 'bg-secondary/40 text-text_secondary'
                    }`}
                    data-testid="delivery-zone-notice"
                  >
                    <p>
                      {language === 'fr' ? 'Zone de livraison : ' : 'Delivery zone: '}
                      <strong className="text-text_primary">{libelleZone(zone, language)}</strong>
                      {' — '}
                      <strong className="text-text_primary">{formatPrice(deliveryFee, language)}</strong>
                    </p>
                    <p className="mt-1">
                      {language === 'fr'
                        ? `Livraison sous ${delaiLivraison('fr')} après confirmation de la commande.`
                        : `Delivered within ${delaiLivraison('en')} after the order is confirmed.`}
                    </p>
                    {minimumNonAtteint && (
                      <p className="mt-1 font-medium">
                        {language === 'fr'
                          ? `Commande minimum de ${formatPrice(MINIMUM_HORS_LIEGE, language)} pour cette zone : il vous manque ${formatPrice(manquant, language)}.`
                          : `Minimum order of ${formatPrice(MINIMUM_HORS_LIEGE, language)} for this zone: ${formatPrice(manquant, language)} to go.`}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Payment Method */}
              <div className="bg-white p-6 rounded-2xl shadow-sm">
                <h2 className="text-lg font-semibold text-text_primary mb-4">{t('paymentMethod')}</h2>
                <div className="space-y-3">
                  {PAYMENT_METHODS_ENABLED.mollie && (
                  <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.payment_method === 'mollie' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}>
                    <input
                      type="radio"
                      name="payment_method"
                      value="mollie"
                      checked={formData.payment_method === 'mollie'}
                      onChange={handleChange}
                      className="w-5 h-5 text-primary"
                      data-testid="payment-mollie"
                    />
                    <CreditCard className="w-6 h-6 text-primary" />
                    <div>
                      <p className="font-medium text-text_primary">{x.paiementEnLigne}</p>
                      <p className="text-sm text-text_secondary">Bancontact, Visa, Mastercard</p>
                    </div>
                  </label>
                  )}

                  {PAYMENT_METHODS_ENABLED.stripe && (
                  <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.payment_method === 'stripe' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}>
                    <input
                      type="radio"
                      name="payment_method"
                      value="stripe"
                      checked={formData.payment_method === 'stripe'}
                      onChange={handleChange}
                      className="w-5 h-5 text-primary"
                      data-testid="payment-stripe"
                    />
                    <CreditCard className="w-6 h-6 text-primary" />
                    <div>
                      <p className="font-medium text-text_primary">{t('creditCard')}</p>
                      <p className="text-sm text-text_secondary">Visa, Mastercard, Apple Pay, Google Pay</p>
                    </div>
                  </label>
                  )}

                  {PAYMENT_METHODS_ENABLED.paypal && (
                  <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.payment_method === 'paypal' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}>
                    <input
                      type="radio"
                      name="payment_method"
                      value="paypal"
                      checked={formData.payment_method === 'paypal'}
                      onChange={handleChange}
                      className="w-5 h-5 text-primary"
                      data-testid="payment-paypal"
                    />
                    <div className="w-6 h-6 flex items-center justify-center">
                      <span className="text-[#003087] font-bold text-sm">PP</span>
                    </div>
                    <div>
                      <p className="font-medium text-text_primary">{t('paypal')}</p>
                      <p className="text-sm text-text_secondary">{x.paiementPaypal}</p>
                    </div>
                  </label>
                  )}

                  <label className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.payment_method === 'cash' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}>
                    <input
                      type="radio"
                      name="payment_method"
                      value="cash"
                      checked={formData.payment_method === 'cash'}
                      onChange={handleChange}
                      className="w-5 h-5 text-primary"
                      data-testid="payment-cash"
                    />
                    <Truck className="w-6 h-6 text-secondary_hover" />
                    <div>
                      <p className="font-medium text-text_primary">
                        {retrait
                          ? (language === 'fr' ? 'Paiement au retrait' : 'Pay on collection')
                          : t('cashOnDelivery')}
                      </p>
                      <p className="text-sm text-text_secondary">
                        {retrait
                          ? (language === 'fr' ? 'Réglez en espèces en magasin' : 'Pay in cash at the store')
                          : x.paiementEspeces}
                      </p>
                    </div>
                  </label>
                </div>
              </div>
              
              {/* Submit Button - For non-PayPal */}
              {formData.payment_method !== 'paypal' && (
                <button
                  type="submit"
                  disabled={isSubmitting || minimumNonAtteint}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-primary text-white font-semibold rounded-full hover:bg-primary_hover transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  data-testid="place-order-button"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t('processing')}
                    </>
                  ) : (
                    t('placeOrder')
                  )}
                </button>
              )}
              
              {/* PayPal Buttons */}
              {formData.payment_method === 'paypal' && (
                <div className="space-y-4">
                  {!showPayPal ? (
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#003087] text-white font-semibold rounded-full hover:bg-[#001f5f] transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="paypal-continue-button"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          {t('processing')}
                        </>
                      ) : (
                        x.continuerPaypal
                      )}
                    </button>
                  ) : (
                    <PayPalScriptProvider options={{ 'client-id': PAYPAL_CLIENT_ID, currency: 'EUR' }}>
                      <PayPalButtons
                        style={{ layout: 'vertical' }}
                        createOrder={(data, actions) => {
                          return actions.order.create({
                            purchase_units: [{
                              amount: { value: total.toFixed(2), currency_code: 'EUR' },
                              description: 'AfriOne BD - Commande'
                            }]
                          });
                        }}
                        onApprove={handlePayPalApprove}
                        onError={(err) => {
                          // Error handled silently
                          toast.error(x.paypalErreur);
                        }}
                      />
                    </PayPalScriptProvider>
                  )}
                </div>
              )}
            </form>
          </div>
          
          {/* Order Summary */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white p-6 rounded-2xl shadow-sm sticky top-24"
            >
              <h2 className="text-lg font-semibold text-text_primary mb-4">{x.resume}</h2>
              
              <ul className="space-y-3 mb-6">
                {items.map(item => (
                  <li key={item.cle} className="flex gap-3" data-testid={`summary-item-${item.cle}`}>
                    <img
                      src={resolveImageUrl(item.product.image_url)}
                      alt={getLocalizedField(item.product, 'name')}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text_primary line-clamp-1">
                        {getLocalizedField(item.product, 'name')}
                      </p>
                      <p className="text-sm text-text_secondary">
                        {x.quantite} {item.quantity}
                        {item.declinaison?.format ? ` · ${item.declinaison.format}` : ''}
                      </p>
                    </div>
                    <span className="font-medium text-text_primary">
                      {formatPrice(prixLigne(item) * item.quantity, language)}
                    </span>
                  </li>
                ))}
              </ul>
              
              {/* Saisie libre, validation par le serveur. */}
              <div className="border-t border-border pt-4">
                <label htmlFor="code-promo" className="block text-sm text-text_secondary mb-2">
                  {language === 'fr' ? 'Code promotionnel' : 'Promotional code'}
                </label>
                <div className="flex gap-2">
                  <input
                    id="code-promo"
                    type="text"
                    value={codeSaisi}
                    onChange={(e) => { setCodeSaisi(e.target.value); setPromoErreur(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); verifierCode(codeSaisi); } }}
                    placeholder={language === 'fr' ? 'Votre code' : 'Your code'}
                    autoComplete="off"
                    className="flex-1 min-w-0 px-3 py-2 border border-border text-sm uppercase placeholder:normal-case focus:outline-none focus:border-primary"
                    data-testid="promo-input"
                  />
                  <button
                    type="button"
                    onClick={() => verifierCode(codeSaisi)}
                    disabled={promoEnCours || !codeSaisi.trim()}
                    className="px-4 py-2 text-sm font-semibold text-white bg-text_primary disabled:opacity-40 hover:opacity-90 transition-opacity whitespace-nowrap"
                    data-testid="promo-apply"
                  >
                    {promoEnCours
                      ? (language === 'fr' ? 'Vérification…' : 'Checking…')
                      : (language === 'fr' ? 'Appliquer' : 'Apply')}
                  </button>
                </div>
                {promoErreur && (
                  <p className="mt-2 text-sm text-red-600" data-testid="promo-error">{promoErreur}</p>
                )}
                {promo && (
                  <div className="mt-2 flex items-center justify-between gap-2 text-sm" data-testid="promo-applied">
                    <span className="text-green-700 font-medium">
                      {promo.libelle || promo.code}
                      {portOffert && (language === 'fr' ? ' · livraison offerte' : ' · free delivery')}
                    </span>
                    <button
                      type="button"
                      onClick={() => { setPromo(null); setCodeSaisi(''); setPromoErreur(''); }}
                      className="text-text_secondary underline hover:text-primary"
                    >
                      {language === 'fr' ? 'Retirer' : 'Remove'}
                    </button>
                  </div>
                )}
              </div>

              <div className="border-t border-border pt-4 space-y-2">
                <div className="flex justify-between text-text_secondary">
                  <span>{t('subtotal')}</span>
                  <span>{formatPrice(subtotal, language)}</span>
                </div>
                {remise > 0 && (
                  <div className="flex justify-between text-green-700">
                    <span>{language === 'fr' ? 'Remise' : 'Discount'} ({promo.code})</span>
                    <span>-{formatPrice(remise, language)}</span>
                  </div>
                )}
                <div className="flex justify-between text-text_secondary">
                  <span>{retrait ? (language === 'fr' ? 'Retrait en magasin' : 'Collect in store') : t('deliveryFee')}</span>
                  {/* Afficher des frais de livraison sur une commande à
                      retirer contredirait le total juste en dessous. */}
                  {retrait ? (
                    <span className="text-green-700 font-medium">
                      {language === 'fr' ? 'Gratuit' : 'Free'}
                    </span>
                  ) : deliveryFee === null ? (
                    <span className="italic text-sm">
                      {language === 'fr' ? 'selon code postal' : 'based on postcode'}
                    </span>
                  ) : portOffert ? (
                    <span>
                      <span className="line-through text-text_secondary/50 mr-1.5">
                        {formatPrice(deliveryFee, language)}
                      </span>
                      <span className="text-green-700 font-medium">
                        {language === 'fr' ? 'offerte' : 'free'}
                      </span>
                    </span>
                  ) : (
                    <span>{formatPrice(deliveryFee, language)}</span>
                  )}
                </div>
                <div className="flex justify-between text-lg font-bold text-text_primary pt-2 border-t border-border">
                  <span>{t('total')}</span>
                  <span className="text-primary" data-testid="checkout-total">{formatPrice(total, language)}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
